import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, Suspense } from 'react';
import { useAtom } from 'jotai';
import { AlertCircle, Download, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { PageHeader } from '@/components/layout/PageHeader';
import { JobFilterBar, JobsTable, AddJobButton } from '@/components/jobs';
import { DataTableSkeleton } from '@/components/ui/data-table-skeleton';
import { useJobs, useBulkUpdateJobs, useDeleteJob, useExportJobs } from '@/hooks/useJobs';
import { jobFiltersAtom, jobPaginationAtom } from '@/store/jobFiltersAtom';
import type { Job } from '@repo/types';

export const Route = createFileRoute('/admin/jobs/')({
  component: JobsPage,
});

function JobsPage() {
  const navigate = useNavigate();
  const [filters] = useAtom(jobFiltersAtom);
  const [pagination, setPagination] = useAtom(jobPaginationAtom);
  const [jobToDelete, setJobToDelete] = useState<Job | null>(null);
  const [jobsToDelete, setJobsToDelete] = useState<string[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data, isLoading, isFetching, error, refetch } = useJobs(filters, pagination);

  const paginationData = data?.pagination || { page: 1, limit: pagination.limit, total: 0 };

  const deleteJobMutation = useDeleteJob();
  const bulkUpdateMutation = useBulkUpdateJobs();
  const exportJobsMutation = useExportJobs();

  // TODO: Fetch from auth context
  const currentUserRole = 'admin';
  const canEdit = true;
  const canDelete = ['team_lead', 'manager', 'admin'].includes(currentUserRole);
  const canBulkAction = ['team_lead', 'manager', 'admin'].includes(currentUserRole);

  const handleRowClick = (job: Job) => navigate({ to: '/admin/jobs/$id/details', params: { id: job.id } });
  const handleEditClick = (job: Job) => navigate({ to: '/admin/jobs/$id/edit', params: { id: job.id } });
  const handleDeleteClick = (job: Job) => setJobToDelete(job);
  const handleDuplicateCheck = (job: Job) =>
    navigate({ to: '/admin/jobs/$id/duplicates', params: { id: job.id } });

  const confirmDelete = async () => {
    if (jobToDelete) {
      await deleteJobMutation.mutateAsync(jobToDelete.id);
      setJobToDelete(null);
    }
  };

  const handleBulkDelete = (jobIds: string[]) => {
    // Bulk delete not supported by backend — delete individually after confirm.
    setJobsToDelete(jobIds);
  };

  const confirmBulkDelete = async () => {
    if (jobsToDelete.length > 0) {
      for (const jobId of jobsToDelete) {
        await deleteJobMutation.mutateAsync(jobId);
      }
      setJobsToDelete([]);
    }
  };

  const handleBulkStatusChange = async (
    jobIds: string[],
    status: 'active' | 'draft' | 'expired'
  ) => {
    await bulkUpdateMutation.mutateAsync({ jobIds, status });
  };

  const handleExport = () => exportJobsMutation.mutate(filters);

  // Refresh = "show me the latest": jump back to page 1 (persisted in
  // localStorage, so a stale later page would hide newly-added jobs) and refetch.
  const handleRefresh = () => {
    setIsRefreshing(true);
    setPagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
    refetch();
    // Guarantee a visible spin even when the refetch resolves instantly (cached).
    window.setTimeout(() => setIsRefreshing(false), 600);
  };

  const refreshing = isRefreshing || isFetching;

  const handlePageSize = (value: string) => {
    setPagination((prev) => ({ ...prev, limit: Number(value), page: 1 }));
  };

  const { page, limit } = paginationData;
  const total = paginationData.total ?? 0;
  const totalPages = total ? Math.max(1, Math.ceil(total / limit)) : 1;
  const rangeStart = total ? (page - 1) * limit + 1 : 0;
  const rangeEnd = Math.min(page * limit, total || 0);

  return (
    <div className="space-y-6 p-6 duration-300 animate-in fade-in">
      <PageHeader
        title="Jobs"
        description="View and manage all job postings across the platform."
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              title="Jump to page 1 and reload the latest jobs"
            >
              <RefreshCw className={`size-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={exportJobsMutation.isPending}
            >
              <Download className="size-4" />
              {exportJobsMutation.isPending ? 'Exporting' : 'Export'}
            </Button>
            <Suspense fallback={<Skeleton className="h-8 w-28" />}>
              <AddJobButton />
            </Suspense>
          </>
        }
      />

      {/* Filters */}
      <Suspense fallback={<Skeleton className="h-10 w-full" />}>
        <JobFilterBar employees={[]} categories={[]} />
      </Suspense>

      {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : 'Failed to load jobs. Please try again.'}
          </AlertDescription>
        </Alert>
      )}

      {/* Loading */}
      {isLoading && <DataTableSkeleton columns={6} />}

      {/* Table */}
      {!isLoading && data && (
        <>
          <Suspense fallback={<DataTableSkeleton columns={6} />}>
            <JobsTable
              data={data?.jobs || []}
              onRowClick={handleRowClick}
              onEditClick={handleEditClick}
              onDeleteClick={handleDeleteClick}
              onDuplicateCheck={handleDuplicateCheck}
              onBulkDelete={handleBulkDelete}
              onBulkStatusChange={handleBulkStatusChange}
              canEdit={canEdit}
              canDelete={canDelete}
              canBulkAction={canBulkAction}
            />
          </Suspense>

          {/* Pagination */}
          {total > 0 && (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">
                Showing <span className="font-medium text-foreground">{rangeStart}</span>–
                <span className="font-medium text-foreground">{rangeEnd}</span> of{' '}
                <span className="font-medium text-foreground">{total}</span> jobs
              </p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Rows</span>
                  <Select value={String(limit)} onValueChange={handlePageSize}>
                    <SelectTrigger className="h-8 w-[72px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[10, 20, 50].map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <span className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                    disabled={page >= totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!jobToDelete} onOpenChange={() => setJobToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Job</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{jobToDelete?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk delete confirmation */}
      <AlertDialog open={jobsToDelete.length > 0} onOpenChange={() => setJobsToDelete([])}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Multiple Jobs</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {jobsToDelete.length} jobs? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmBulkDelete} className="bg-destructive hover:bg-destructive/90">
              Delete {jobsToDelete.length} Jobs
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
